import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CircularProgress, useMediaQuery } from '@material-ui/core'
import { isEqual } from 'lodash'
import { useHistory } from 'react-router-dom'
import {
  AdCreative,
  AssetApprovalStatus,
  CreativeCopyPatch,
  CreativeMediaRef,
  LeadCard,
} from '@marketing-milk/interfaces'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { getApiErrorMessage } from 'app/util'
import { LocalAdPreview, LocalAdPreviewPlacement } from 'components/ad-preview'
import { DynamicPoolPicker } from './DynamicPoolPicker'
import { ReviewCopyEditor } from './ReviewCopyEditor'
import { copyDraftFromCreative, copyDraftToPreview, draftPools } from './copyDraft'
import { PROCESSING_NOTE } from './MediaControls'
import {
  DraftCard,
  MediaUploadTarget,
  UploadedMedia,
  UploadedMediaMap,
  draftAssets,
  draftCards,
  draftSingleMedia,
  mediaKey,
  withAssetAdded,
  withAssetRemoved,
  withCardAdded,
  withCardCopy,
  withCardMedia,
  withCardRemoved,
  withSingleMedia,
} from './mediaDraft'
import { REVIEW_MEDIA_ACCEPT } from './uploadReviewMedia'
import {
  DynamicCombination,
  FIRST_COMBINATION,
  clampCombination,
  copyPools,
  dynamicPools,
  pinnedCombination,
} from './dynamicCombination'
import { VerdictButton } from './VerdictButton'
import { useHistoryFits } from './ReviewHistory'
import { CreativeJourneyPanel, getCreativeJourney } from '../components/CreativeJourney'
import {
  MODAL_SCROLL_CLASS,
  PacketModalShell,
  modalGhostButtonStyle,
  modalInputStyle,
  modalPrimaryButtonStyle,
  modalScrollFadeStyle,
} from '../components/PacketModalShell'
import { dark, radius, status as statusColors, type as typo } from '../tokens'

type Props = {
  creative: AdCreative
  /** Pre-opens the reason form — used when the caller clicked Reject on the card. */
  startInReject?: boolean
  /**
   * Which ad this is out of how many in the host's own order — a packet counts ads
   * only, since theme slides aren't numbered. Absent when the ad isn't in that
   * order at all, as with a link to an ad the host hasn't loaded.
   */
  position?: { current: number; total: number }
  /** The theme this ad sits under, shown so the slide's framing survives the walk. */
  themeLabel?: string
  hasPrev: boolean
  hasNext: boolean
  /** Prevent edits while the host loads the ad it is navigating to. */
  navigationPending?: boolean
  onPrev: () => void
  onNext: () => void
  /**
   * Record a verdict. The page owns the mutation for the whole grid, so the modal
   * and the cards can't send different payloads. A capability like the editing
   * ones below: a host that isn't asking for approval — the creative library —
   * leaves it out, and the dialog is then an editor with no verdict at all.
   */
  onSubmitVerdict?: (verdict: { approved: boolean; notes?: string }) => Promise<unknown>
  submitting?: boolean
  /**
   * Employee-only affordances, supplied as capabilities rather than gated on a
   * cohort flag — the page decides who gets what, once, and this modal never
   * learns what a permission is.
   */
  onRemoveApproval?: () => void
  /**
   * Commit the edited ad and hand back what was saved — including ids for cards
   * this save created, which the browser cannot derive. A capability like the two
   * around it, and its absence is what makes the column read-only for a client.
   */
  onSaveCopy?: (patch: CreativeCopyPatch) => Promise<AdCreative>
  /** Upload one replacement asset. Supplied alongside `onSaveCopy`, to employees. */
  onUploadMedia?: (file: File) => Promise<UploadedMedia>
  /**
   * The ad's history and comments column. A capability like the two above: the
   * page withholds it from clients, and the modal only gives it a cell. Its own
   * open state must be held by the page too — this modal is remounted per ad.
   */
  historySlot?: React.ReactNode
  /**
   * Open the ad's comments some other way. Offered only while the history column
   * doesn't fit, so a narrow window keeps a way to read and leave comments.
   */
  onOpenComments?: () => void
  /** Called after a successful approve/reject — moves to the next ad, or closes if this was the last one. */
  onAdvance?: () => void
  onClose: () => void
}

const SHELL_PADDING = 20

const DISCARD_PROMPT = 'Discard your unsaved changes?'

/**
 * The body fills whatever the shell has left rather than computing a height from
 * the viewport. The arithmetic version was off by the card's own two border
 * pixels, which made the shell scrollable by that much — a full-height scrollbar
 * beside the copy column's, scrolling nothing.
 */
const BODY_FILL: React.CSSProperties = { flex: 1, minHeight: 0 }

/** Natural width of the Facebook mobile-feed render; the preview is scaled, not reflowed. */
const PREVIEW_WIDTH = 360

/**
 * The ad's column is a fixed size rather than a percentage of the dialog. A phone
 * render has a natural width, and pinning it is what lets the history column open
 * without the ad resizing under a reviewer mid-decision. Wide enough to hold the
 * lead card's phone chrome too, which is a different frame from the ad's.
 */
const AD_COLUMN_WIDTH = 440

/** Below this the two columns can't both hold their minimums; the layout drops to one. */
const COPY_COLUMN_MIN = 380

/** Line length, not track width — a wide dialog leaves whitespace rather than 800px inputs. */
const COPY_CONTENT_MAX = 520

const COLUMN_GAP = 20

/** Dialog widths by the regions present, so a two-column client dialog isn't sized for three. */
const CLIENT_WIDTH = AD_COLUMN_WIDTH + COLUMN_GAP + COPY_CONTENT_MAX + SHELL_PADDING * 2
const EMPLOYEE_WIDTH = CLIENT_WIDTH + COLUMN_GAP + 360

/**
 * The narrow composition's floor: both columns at their minimums, plus the shell's
 * padding and the overlay's inset. Deliberately not `useScreenSize` — that only
 * knows about phone width, and everything between it and this would clip.
 */
const TWO_COLUMN_MIN = AD_COLUMN_WIDTH + COLUMN_GAP + COPY_COLUMN_MIN + SHELL_PADDING * 2 + 32

/**
 * The preview is allowed to grow past its natural size — the ad is the thing
 * being judged, and a 360px phone render in a full-height column otherwise
 * leaves most of that column empty. Capped low: the media is a bitmap, and past
 * this the ad's own type dwarfs the interface type beside it, which reads as the
 * dialog being out of proportion rather than the ad being prominent.
 */
const MAX_PREVIEW_SCALE = 1.15

/**
 * Whether the server definitely refused the save before writing anything. An HTTP
 * error status alone does not say that — a gateway can report a failure after the
 * application committed — so only the statuses the API itself uses to reject a
 * request count.
 */
const DEFINITE_REJECTIONS = [400, 401, 403, 404, 409, 422]
const definitelyRejected = (error: any) => DEFINITE_REJECTIONS.includes(error?.response?.status)

function applyUpload(
  draft: CreativeCopyPatch,
  target: MediaUploadTarget,
  ref: CreativeMediaRef
): CreativeCopyPatch {
  switch (target.kind) {
    case 'single':
      return withSingleMedia(draft, ref)
    case 'card':
      return withCardMedia(draft, target.card, ref)
    case 'add-card':
      return withCardAdded(draft, ref)
    case 'add-asset':
      return withAssetAdded(draft, ref)
    // A dynamic ad's assets are a set with no slots, so replacing one is dropping
    // it and adding the other.
    case 'asset':
      return withAssetAdded(withAssetRemoved(draft, target.replacing), ref)
  }
}

const ctaLabel = (value?: string) =>
  value
    ? value
        .split('_')
        .map(word => word[0] + word.slice(1).toLowerCase())
        .join(' ')
    : undefined

/**
 * Shrink the ad preview to whatever height is left, instead of letting it decide
 * the dialog's height.
 *
 * It has to be measured rather than set: with a local fallback rendered the
 * preview's height is `auto` (the card's natural height at its fixed width), and
 * that varies by creative — a 4:5 image is far taller than a 1:1 — so no fixed
 * height fits every ad. Scaling also has to re-run after the fact, because the
 * journey step swaps in a differently-shaped frame.
 */
function useScaleToFit(deps: unknown[]) {
  const outerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (!outer || !inner) return undefined

    // `transform` doesn't affect layout size, so `scrollHeight` stays the natural
    // height and re-measuring can't feed back into itself.
    const measure = () => {
      const naturalHeight = inner.scrollHeight
      const naturalWidth = inner.scrollWidth
      if (!naturalHeight || !naturalWidth) return
      setScale(
        Math.min(
          outer.clientHeight / naturalHeight,
          outer.clientWidth / naturalWidth,
          MAX_PREVIEW_SCALE
        )
      )
    }

    measure()
    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(measure)
    observer.observe(outer)
    observer.observe(inner)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { outerRef, innerRef, scale }
}

/** One line of status under the verdict buttons. */
const Note = ({ tone, children }: { tone: string; children: React.ReactNode }) => (
  <span
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontFamily: typo.family.sans,
      fontSize: typo.size.body,
      color: tone,
    }}
  >
    {children}
  </span>
)

/** One label/value row in the metadata panel; renders nothing when value is empty. */
const MetaRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.eyebrow,
        fontWeight: typo.weight.semibold,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: dark.textFaint,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.body,
        color: dark.text,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  </div>
)

/** A pair of view choices under the ad, one of them always on. */
function Segmented({
  label,
  segments,
}: {
  label: string
  segments: Array<{ label: string; on: boolean; onClick: () => void }>
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{
        display: 'flex',
        padding: 2,
        gap: 2,
        borderRadius: radius.sm,
        border: `1px solid ${dark.hairline}`,
        background: dark.bg,
      }}
    >
      {segments.map(segment => (
        <button
          key={segment.label}
          type="button"
          role="tab"
          aria-selected={segment.on}
          onClick={segment.onClick}
          style={{
            padding: '5px 14px',
            borderRadius: radius.sm - 2,
            border: 'none',
            background: segment.on ? dark.surface : 'transparent',
            fontFamily: typo.family.sans,
            fontSize: typo.size.body,
            fontWeight: segment.on ? typo.weight.semibold : typo.weight.regular,
            color: segment.on ? dark.text : dark.textMuted,
            cursor: 'pointer',
          }}
        >
          {segment.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Prev / next / close, as a recessed well rather than a bordered circle.
 *
 * These sat on `dark.surface` — the shell's own colour — so the only thing
 * separating a button from the panel behind it was a `#2a2a2e` hairline, which
 * on this ground is very close to nothing. Reviewers reported not finding them.
 * Dropping the fill to `dark.bg` reads as a well punched into the panel and
 * costs no extra chrome, so the header stays as quiet as it was.
 */
function HeaderIconButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: string
  label: string
  onClick: () => void
  disabled: boolean
}) {
  const [hover, setHover] = useState(false)
  const live = hover && !disabled

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      style={{
        flexShrink: 0,
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: `1px solid ${live ? dark.accent : dark.hairline}`,
        background: live ? dark.hairline : dark.bg,
        color: dark.text,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'border-color 0.15s ease, color 0.15s ease',
      }}
    >
      <i className={`fa fa-${icon}`} style={{ color: 'inherit' }} aria-hidden="true" />
    </button>
  )
}

/**
 * The client's full-screen-ish view of one ad: the ad at a real size beside the
 * copy Facebook will show, plus the same approve/reject actions as the card.
 * Rejecting reveals an inline reason field here instead of stacking a second
 * modal on top. A successful decision advances to the next ad (slideshow-style)
 * rather than closing — the parent decides what "next" means and closes instead
 * once there isn't one. The verdict is the host's to offer: the creative library
 * opens the same dialog with none, as the place an ad is edited.
 *
 * One grid rather than nested flex rows, because five other issues each add a
 * region to this dialog and none of them can see the others. Wide and narrow
 * differ only in which areas the grid places, so the header and the verdict bar
 * are written once and never move between wrappers.
 *
 * The dialog is sized by the viewport and by which regions are present, not by
 * its contents. The verdict buttons used to sit at the end of the scrolling copy
 * column, which put the page's primary action below the fold on a laptop whenever
 * the copy ran long.
 */
export function ReviewCreativePreviewModal({
  creative,
  startInReject = false,
  position,
  themeLabel,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  navigationPending = false,
  onSubmitVerdict,
  submitting = false,
  onRemoveApproval,
  onSaveCopy,
  onUploadMedia,
  historySlot,
  onOpenComments,
  onAdvance,
  onClose,
}: Props) {
  const [rejecting, setRejecting] = useState(startInReject)
  const [reason, setReason] = useState('')

  // Step two of the journey — reached deliberately, never automatically. Lead
  // cards repeat across a packet, so surfacing one on all fifty approvals is
  // noise rather than diligence.
  const [showJourney, setShowJourney] = useState(false)
  // Feed is where most of these ads are seen; Story is the second look. Per ad,
  // since the dialog is remounted for each.
  const [placement, setPlacement] = useState<LocalAdPreviewPlacement>('feed')

  const isNarrow = !useMediaQuery(`(min-width:${TWO_COLUMN_MIN}px)`)
  const historyFits = useHistoryFits()
  const hasHistory = !!historySlot && historyFits && !isNarrow

  // A published ad is live on Facebook; Published already wins the computed status,
  // so the server refuses a verdict on one. Hide the actions rather than offer a
  // button that 400s.
  const hasVerdict = !!onSubmitVerdict
  const canReview = hasVerdict && creative.reviewStatus !== AssetApprovalStatus.Published

  // Each verdict button shows its own recorded state, so a re-press undoes or
  // revises it. Only an employee can lift an approval — for a client the pressed
  // Approve is a status, and the way back is a rejection, which the server treats
  // as superseding the approval outright.
  const isApproved = creative.reviewStatus === AssetApprovalStatus.Approved
  const isRejected = creative.reviewStatus === AssetApprovalStatus.Rejected
  const canRemoveApproval = !!onRemoveApproval && isApproved
  // A rejection now withholds the ad from every draft campaign, so the way back out
  // has to be on this screen. It can't ride the reject button's second press —
  // that revises the reason, which is the act a rejected ad gets asked for far more
  // often — so the clear stands beside the pair as its own thing.
  const canClearRejection = !!onRemoveApproval && isRejected

  // Dynamic creatives have no single "the ad": Facebook assembles a combination
  // at delivery. Rather than list the parts, pin one combination at a time so the
  // reviewer judges assembled ads — which is what they are being asked to approve.
  const isDynamic = isDynamicCreative(creative)
  const savedPools = dynamicPools(creative)
  const [requestedCombination, setCombination] = useState<DynamicCombination>(FIRST_COMBINATION)

  // Editing. The modal is remounted per ad, so the draft is initialised once from
  // the creative and needs no effect to follow it — and after a save it is rebuilt
  // from the response, so the refetch behind it can't flash the fields back.
  const canEditCopy = !!onSaveCopy
  const [draft, setDraft] = useState<CreativeCopyPatch>(() => copyDraftFromCreative(creative))
  const [saved, setSavedDraft] = useState<CreativeCopyPatch>(draft)
  const [savingCopy, setSavingCopy] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // An addition whose outcome we could not read cannot be retried blind.
  const [saveBlocked, setSaveBlocked] = useState(false)
  const [confirmingCopy, setConfirmingCopy] = useState(false)
  const copyDirty = canEditCopy && !isEqual(draft, saved)
  // The server refuses a blank name, so Save waits for one rather than failing.
  const cannotSave = saveBlocked || !(draft.name ?? '').trim()

  // Uploaded assets the draft references but the saved creative doesn't know about
  // yet — the URLs the tiles and the preview draw from. Keyed by type and id.
  const [uploads, setUploads] = useState<UploadedMediaMap>({})
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [selectedCardKey, setSelectedCardKey] = useState<string | undefined>(undefined)
  const fileInput = useRef<HTMLInputElement>(null)
  const uploadTarget = useRef<MediaUploadTarget | null>(null)
  /**
   * Which upload the surface is still waiting for. Bumped when the reviewer
   * discards or leaves, so an abandoned POST that lands afterwards cannot write
   * into a draft that has moved on. The upload itself is left to finish — an
   * unused asset row costs nothing.
   */
  const uploadGeneration = useRef(0)
  useEffect(() => () => void (uploadGeneration.current += 1), [])

  // A published ad is live on Facebook and the server refuses the write, so the
  // fields and the media controls state why rather than accepting edits that
  // can't be saved.
  const publishedReason =
    creative.reviewStatus === AssetApprovalStatus.Published
      ? 'This ad is published to Facebook, so it can no longer be changed here.'
      : undefined

  // The save's response replaces the draft outright, so anything typed or removed
  // while it is in flight would vanish when it lands. Uploads deliberately do not
  // lock the fields — a slow upload is exactly when copy gets fixed.
  const contentLocked = !!publishedReason || savingCopy || navigationPending

  // While editing, what the picker lists and the ad is assembled from is the
  // drafted state — otherwise a variant you just typed, or an asset you just
  // uploaded, wouldn't be selectable.
  const assets = canEditCopy ? draftAssets(creative, draft, uploads) : savedPools.assets
  const pools = canEditCopy ? draftPools(savedPools, draft, assets) : savedPools
  const poolList = copyPools(pools, canEditCopy)
  const combination = clampCombination(pools, requestedCombination)
  const cards = canEditCopy ? draftCards(creative, draft, uploads) : []
  const mediaView = {
    single: canEditCopy ? draftSingleMedia(creative, draft, uploads) : undefined,
    cards,
    pools,
  }
  // Selection can still move while a save is in flight — the card tabs and the
  // pinned combination are view controls, not edits — so the response maps
  // forward whatever is selected when it lands, not what was when Save was pressed.
  const selection = useRef({ card: selectedCardKey, asset: combination.asset })
  selection.current = { card: selectedCardKey, asset: combination.asset }

  const shownCreative = canEditCopy
    ? copyDraftToPreview(creative, draft, combination, mediaView)
    : isDynamic
    ? pinnedCombination(creative, savedPools, combination)
    : creative

  // Where the ad goes after the tap. The packet's own resolver rather than a
  // second implementation: it reads `leadCardUsed`, prefers `linkWebsiteURL` over
  // the Facebook permalink, and prefixes a scheme-less URL. Read from the draft,
  // so a form picked or a destination typed shows before it is saved.
  const [pickedLeadCard, setPickedLeadCard] = useState<LeadCard>()
  const journey = getCreativeJourney({
    ...creative,
    linkWebsiteURL: draft.url ?? creative.linkWebsiteURL,
    linkDisplayURL: draft.displayLink ?? creative.linkDisplayURL,
    leadCardUsed:
      draft.leadCardID === undefined
        ? creative.leadCardUsed
        : draft.leadCardID === null
        ? undefined
        : pickedLeadCard,
  })
  const business = useGetBusinessIfExists()

  // Re-measure whenever the thing being measured can change shape: the journey
  // step swapping in a differently-sized frame, or the reason form opening under
  // it. Typing needs no dependency of its own — the observer watches the rendered
  // card, so copy that changes its height re-measures on its own.
  const { outerRef, innerRef, scale } = useScaleToFit([
    isDynamic,
    rejecting,
    showJourney,
    hasHistory,
    placement,
  ])

  const trimmedReason = reason.trim()

  // A verdict is unavailable while a save or an upload is in flight, and vice
  // versa: the save clears an approval, so an Approve landing between the request
  // and its response would be wiped by a write it couldn't see, and an upload is
  // about to make the draft dirty.
  const busy = submitting || savingCopy || uploading || navigationPending
  const verdictBlocked = busy || copyDirty

  const handleApprove = async () => {
    if (!onSubmitVerdict || verdictBlocked) return
    await onSubmitVerdict({ approved: true })
    onAdvance?.()
  }

  const handleSubmitReject = async () => {
    if (!onSubmitVerdict || !trimmedReason || verdictBlocked) return
    await onSubmitVerdict({ approved: false, notes: trimmedReason })
    onAdvance?.()
  }

  const handleSaveCopy = async () => {
    if (!onSaveCopy || !copyDirty || busy || cannotSave) return
    // Adding a card is not idempotent, so a save whose outcome we cannot read
    // cannot be retried on the reviewer's behalf.
    const adding = (draft.media?.slides?.add ?? []).length > 0
    setSavingCopy(true)
    setSaveError(null)
    try {
      const savedCreative = await onSaveCopy(draft)
      // The response is the new baseline — it carries the ids of cards this save
      // created, which a re-send of the same draft would create a second time.
      const next = copyDraftFromCreative(savedCreative)
      setDraft(next)
      setSavedDraft(next)
      setUploads({})
      setConfirmingCopy(false)
      keepSelections(savedCreative, next)
    } catch (e) {
      if (adding && !definitelyRejected(e)) {
        setSaveBlocked(true)
        setSaveError(
          "We couldn't confirm that save, so the new card may or may not have been added. Reload this ad to see where it stands."
        )
        return
      }
      // The draft is left exactly as typed: the retry is the same button, not a
      // re-type. `saved` is untouched, so it stays dirty and stays enabled.
      const retry = 'Your changes are still here — try again.'
      // A 400 carries the rule the edit broke (a lead form's buttons, a Call Now
      // number), which the fields alone can't always show.
      setSaveError(
        (e as any)?.response?.status === 400
          ? `That didn't save: ${getApiErrorMessage(e).replace(/\.$/, '')}. ${retry}`
          : `That didn't save. ${retry}`
      )
    } finally {
      setSavingCopy(false)
    }
  }

  /**
   * Keep the reviewer looking at the same card and the same pairing across a save.
   * By identity, not by index: the server returns a dynamic ad's images before its
   * videos, so an added image lands in the middle of the list the editor showed it
   * at the end of.
   */
  const keepSelections = (savedCreative: AdCreative, nextDraft: CreativeCopyPatch) => {
    const selectedCard = cards.find(card => card.key === selection.current.card)
    if (selectedCard) {
      const nextCards = draftCards(savedCreative, nextDraft, {})
      const survivor =
        selectedCard.id !== undefined
          ? nextCards.find(card => card.id === selectedCard.id)
          : // A card that had no id has just been given one, appended in the order
            // it was added.
            nextCards[
              nextCards.length - (draft.media?.slides?.add ?? []).length + selectedCard.addIndex!
            ]
      setSelectedCardKey(survivor?.key)
    }

    const pinnedRef = pools.assets[selection.current.asset]
    if (pinnedRef) {
      const at = dynamicPools(savedCreative).assets.findIndex(
        asset => mediaKey(asset) === mediaKey(pinnedRef)
      )
      if (at >= 0) setCombination(current => ({ ...current, asset: at }))
    }
  }

  // Back to the last saved state. Deliberately unconfirmed: the button names what
  // it does and is only there while there is something to lose, where the routes
  // that discard by accident — closing, or paging to another ad — go through
  // `requestLeave` and ask.
  const discardCopy = () => {
    uploadGeneration.current += 1
    setDraft(saved)
    setUploads({})
    setUploading(false)
    setUploadError(null)
    setSaveError(null)
    setConfirmingCopy(false)
  }

  /** Ask for a file; `handleFile` applies whatever comes back to this target. */
  const requestUpload = (target: MediaUploadTarget) => {
    if (busy) return
    uploadTarget.current = target
    setUploadError(null)
    if (fileInput.current) fileInput.current.value = ''
    fileInput.current?.click()
  }

  const handleFile = async (file: File) => {
    const target = uploadTarget.current
    if (!onUploadMedia || !target) return
    const generation = ++uploadGeneration.current
    setUploading(true)
    setUploadError(null)
    try {
      const media = await onUploadMedia(file)
      if (uploadGeneration.current !== generation) return
      const ref: CreativeMediaRef = { mediaType: media.mediaType, mediaID: media.asset.id }
      setUploads(current => ({ ...current, [mediaKey(ref)]: media }))
      setDraft(current => applyUpload(current, target, ref))
      moveSelectionTo(target)
    } catch (e: any) {
      if (uploadGeneration.current !== generation) return
      setUploadError(e?.response?.data?.message ?? e?.message ?? "That file didn't upload.")
    } finally {
      if (uploadGeneration.current === generation) setUploading(false)
    }
  }

  /** Show the reviewer what they just uploaded, rather than what was selected before. */
  const moveSelectionTo = (target: MediaUploadTarget) => {
    if (target.kind === 'add-card') {
      setSelectedCardKey(`new:${(draft.media?.slides?.add ?? []).length}`)
    }
    // A dynamic addition and a replacement both land at the end of the list.
    if (target.kind === 'add-asset') setCombination(at => ({ ...at, asset: assets.length }))
    if (target.kind === 'asset') setCombination(at => ({ ...at, asset: assets.length - 1 }))
  }

  // The client approved this ad; saving a different one removes their approval,
  // and the strategist is the one who should decide that in the moment.
  const needsApprovalWarning = creative.reviewStatus === AssetApprovalStatus.Approved
  const requestSaveCopy = () =>
    cannotSave ? undefined : needsApprovalWarning ? setConfirmingCopy(true) : handleSaveCopy()

  // Said once the reviewer has staged a video, not as a live status: playback in
  // the preview is our own stored copy, and the save carries the Facebook
  // identifier the upload already persisted.
  const hasStagedVideo = Object.values(uploads).some(upload => upload.mediaType === 'video')

  // Without a verdict the bar holds only copy work, so at rest there is nothing to
  // pin and no hairline to draw above it.
  const hasCopyWork = copyDirty || uploading || hasStagedVideo || !!uploadError || !!saveError
  const showActions = hasVerdict ? canReview || rejecting : confirmingCopy || hasCopyWork

  // One gate for every visible way out of the modal. A save in flight cannot be
  // abandoned because its failure would have nowhere to surface; a dirty draft
  // may be discarded, but only deliberately.
  const leaveConfirmed = useRef(false)
  const requestLeave = (leave: () => void) => {
    if (savingCopy) return
    if (copyDirty || uploading) {
      if (!window.confirm(DISCARD_PROMPT)) return
      // The navigation `leave` makes may land after an await (paging past the
      // loaded list), so the blocker below lets the next one through rather
      // than asking twice.
      leaveConfirmed.current = true
    }
    // Whatever an abandoned upload eventually returns belongs to an ad nobody is
    // looking at any more.
    uploadGeneration.current += 1
    leave()
  }

  // Every other way off the ad skips the gate above: Back and Forward, and any
  // link on the page behind it. Hosts keep the open ad in the URL, so leaving
  // it is a change of page or of `?ad=`; a query param that only rides along
  // (a comment link's) is not.
  const history = useHistory()
  useEffect(() => {
    if (!copyDirty && !savingCopy && !uploading) return undefined
    return history.block(location => {
      const current = history.location
      const adOf = (search: string) => new URLSearchParams(search).get('ad')
      if (location.pathname === current.pathname && adOf(location.search) === adOf(current.search))
        return undefined
      if (leaveConfirmed.current) {
        leaveConfirmed.current = false
        return undefined
      }
      return savingCopy ? false : DISCARD_PROMPT
    })
  }, [history, copyDirty, savingCopy, uploading])

  // The page owns document-level arrow navigation, outside this modal's props.
  // Suppress that shortcut while leaving would lose or hide copy work; the
  // visible Previous/Next buttons still go through `requestLeave` above.
  useEffect(() => {
    if (!copyDirty && !savingCopy && !uploading) return undefined
    const blockArrowNavigation = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') event.stopImmediatePropagation()
    }
    document.addEventListener('keydown', blockArrowNavigation, true)
    return () => document.removeEventListener('keydown', blockArrowNavigation, true)
  }, [copyDirty, savingCopy, uploading])

  const narrowHasCopy = isDynamic || canEditCopy

  const gridStyle: React.CSSProperties = isNarrow
    ? {
        display: 'grid',
        ...BODY_FILL,
        gap: COLUMN_GAP,
        gridTemplateColumns: 'minmax(0, 1fr)',
        // A dynamic ad's pools stack under it rather than being dropped: they are
        // the only place its other variants exist, and clients — the cohort most
        // likely to be on a phone — would otherwise approve copy they never saw.
        // The ad keeps the larger share; the pools scroll in what is left.
        gridTemplateRows: narrowHasCopy
          ? 'auto minmax(0, 1.4fr) minmax(0, 1fr) auto'
          : 'auto minmax(0, 1fr) auto',
        gridTemplateAreas: narrowHasCopy
          ? '"header" "preview" "copy" "actions"'
          : '"header" "preview" "actions"',
      }
    : {
        display: 'grid',
        ...BODY_FILL,
        gap: COLUMN_GAP,
        gridTemplateColumns: hasHistory
          ? `${AD_COLUMN_WIDTH}px minmax(${COPY_COLUMN_MIN}px, 1fr) auto`
          : `${AD_COLUMN_WIDTH}px minmax(${COPY_COLUMN_MIN}px, 1fr)`,
        gridTemplateRows: 'auto minmax(0, 1fr) auto',
        gridTemplateAreas: hasHistory
          ? '"preview header history" "preview copy history" "preview actions history"'
          : '"preview header" "preview copy" "preview actions"',
      }

  return (
    <PacketModalShell
      palette={dark}
      hideHeader
      onClose={() => requestLeave(onClose)}
      maxWidth={historySlot && historyFits ? EMPLOYEE_WIDTH : CLIENT_WIDTH}
      padding={SHELL_PADDING}
      fillHeight
      closeOnBackdropClick
    >
      <div style={gridStyle}>
        {/* The ad owns its area outright, top to bottom. It is the thing being
          judged, so nothing shares its side and nothing above it steals height. */}
        <div
          style={{
            gridArea: 'preview',
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div
            ref={outerRef}
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // No border: the ad is already a card with its own edge, and framing
              // a frame is what made this column read as an empty panel with
              // something small inside it. The recessed ground alone separates them.
              background: dark.bg,
              borderRadius: radius.md,
              padding: 12,
              overflow: 'hidden',
            }}
          >
            {showJourney && journey ? (
              <div ref={innerRef} style={{ transform: `scale(${scale})` }}>
                <CreativeJourneyPanel journey={journey} business={business} palette={dark} />
              </div>
            ) : (
              <div
                ref={innerRef}
                style={{
                  width: PREVIEW_WIDTH,
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                }}
              >
                {/* Our own rendering, never Facebook's. Facebook's preview of an
                  unlaunched ad is our stored spec handed back to us — drawn without
                  the enhancement opt-out the real ad ships with — so it is not the
                  authority it looks like, and waiting on it cost a round-trip per ad
                  across a fifty-ad packet the client walks alone. */}
                <LocalAdPreview
                  creative={shownCreative}
                  pageName={business?.businessName}
                  pageImageURL={business?.fbPageImgURL}
                  placement={placement}
                />
              </div>
            )}
          </div>

          <div
            style={{
              alignSelf: 'center',
              flexShrink: 0,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            {/* Where the ad is seen, which the journey step has no version of — so
              it steps aside while that is showing rather than doing nothing. */}
            {!showJourney && (
              <Segmented
                label="Placement"
                segments={[
                  { label: 'Feed', on: placement === 'feed', onClick: () => setPlacement('feed') },
                  {
                    label: 'Story',
                    on: placement === 'story',
                    onClick: () => setPlacement('story'),
                  },
                ]}
              />
            )}
            {/* The journey is a second step of the same phone, walked deliberately.
              The control lives beside the frame rather than on it: an overlay to
              catch a tap on the ad would swallow video playback and carousel
              swiping, and the ad is the one thing the reviewer is judging.

              Two segments rather than one toggling button, because the pair states
              what a single "See the lead form →" only implied: this is one phone
              with two steps, and you can be on either. */}
            {journey && (
              <Segmented
                label="Preview step"
                segments={[
                  { label: 'Ad', on: !showJourney, onClick: () => setShowJourney(false) },
                  {
                    label: journey.kind === 'lead-form' ? 'Lead form' : 'Where it goes',
                    on: showJourney,
                    onClick: () => setShowJourney(true),
                  },
                ]}
              />
            )}
          </div>
        </div>

        {/* Position and navigation sit over the copy rather than across the dialog,
          so the ad's column starts at the very top. */}
        <div
          style={{
            gridArea: 'header',
            maxWidth: isNarrow ? undefined : COPY_CONTENT_MAX,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 0,
          }}
        >
          <div
            style={{
              minWidth: 0,
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: dark.textFaint,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {/* Keep room for the theme alongside the navigation controls. */}
            {position && `${position.current}/${position.total}`}
            {position && themeLabel ? ' · ' : ''}
            {themeLabel}
          </div>
          {/* Prev/next belong to the dialog, not the screen. Pinned to the viewport
            edges they float over the ad itself on anything short of a wide
            desktop, which is most of the laptops this is reviewed on. */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {onOpenComments && !hasHistory && (
              <HeaderIconButton
                icon="comment"
                label="Comments"
                onClick={onOpenComments}
                disabled={false}
              />
            )}
            <HeaderIconButton
              icon="chevron-left"
              label="Previous"
              onClick={() => requestLeave(onPrev)}
              disabled={!hasPrev || savingCopy}
            />
            <HeaderIconButton
              icon="chevron-right"
              label="Next"
              onClick={() => requestLeave(onNext)}
              disabled={!hasNext || savingCopy}
            />
            <HeaderIconButton
              icon="times"
              label="Close"
              onClick={() => requestLeave(onClose)}
              disabled={savingCopy}
            />
          </div>
        </div>

        {/* A read-only static ad's copy is already drawn on the ad, so that
          duplicate column can drop at narrow widths. Editing always survives;
          dynamic pools do too because they contain variants the preview can only
          show one of. */}
        {(!isNarrow || narrowHasCopy) && (
          <div
            style={{
              gridArea: 'copy',
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              maxWidth: COPY_CONTENT_MAX,
              // Holds the fade that stands in for the hidden scrollbar.
              position: 'relative',
            }}
          >
            <div
              className={MODAL_SCROLL_CLASS}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                // Room for the fade, so the last field's own text never sits under it.
                paddingBottom: 20,
              }}
            >
              {canEditCopy ? (
                <ReviewCopyEditor
                  creative={creative}
                  draft={draft}
                  onChange={setDraft}
                  onPickLeadCard={setPickedLeadCard}
                  disabled={contentLocked}
                  media={{
                    ...mediaView,
                    selectedCardKey,
                    onSelectCard: setSelectedCardKey,
                    onEditCard: (card, changes) =>
                      setDraft(current => withCardCopy(current, card, changes)),
                    editing: onUploadMedia
                      ? {
                          disabled: contentLocked,
                          uploading,
                          onUpload: requestUpload,
                          onRemoveCard: (card: DraftCard) =>
                            setDraft(current => withCardRemoved(current, card)),
                          onRemoveAsset: ref => setDraft(current => withAssetRemoved(current, ref)),
                        }
                      : undefined,
                  }}
                  disabledReason={publishedReason}
                  dynamic={
                    isDynamic
                      ? {
                          assets: pools.assets,
                          pools: poolList,
                          at: combination,
                          onPick: setCombination,
                        }
                      : undefined
                  }
                />
              ) : isDynamic ? (
                <DynamicPoolPicker
                  assets={pools.assets}
                  pools={poolList}
                  at={combination}
                  onPick={setCombination}
                />
              ) : (
                <>
                  {creative.primaryText && (
                    <MetaRow label="Primary text">{creative.primaryText}</MetaRow>
                  )}
                  {creative.linkHeadline && (
                    <MetaRow label="Headline">{creative.linkHeadline}</MetaRow>
                  )}
                  {creative.linkDescription && (
                    <MetaRow label="Description">{creative.linkDescription}</MetaRow>
                  )}
                  {ctaLabel(creative.callToAction) && (
                    <MetaRow label="Call to action">{ctaLabel(creative.callToAction)}</MetaRow>
                  )}
                </>
              )}
            </div>
            <div style={modalScrollFadeStyle(dark)} />
          </div>
        )}

        {hasHistory && <div style={{ gridArea: 'history', minHeight: 0 }}>{historySlot}</div>}

        {/* Pinned: a verdict is the point of the page, so it never scrolls away —
          and without one, neither does the save for the edits being made. */}
        {showActions && (
          <div
            style={{
              gridArea: 'actions',
              borderTop: `1px solid ${dark.hairline}`,
              paddingTop: 14,
            }}
          >
            {rejecting ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label
                  style={{
                    fontFamily: typo.family.sans,
                    fontSize: typo.size.body,
                    fontWeight: typo.weight.semibold,
                    color: dark.text,
                  }}
                >
                  Reason *
                </label>
                <textarea
                  autoFocus
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  placeholder="What should change?"
                  style={{ ...modalInputStyle(dark), resize: 'none' }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setRejecting(false)}
                    disabled={busy}
                    style={modalGhostButtonStyle(dark)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitReject}
                    disabled={!trimmedReason || verdictBlocked}
                    style={{
                      ...modalPrimaryButtonStyle(dark),
                      flex: '0 0 auto',
                      background: statusColors.danger,
                      borderColor: statusColors.danger,
                      opacity: !trimmedReason || verdictBlocked ? 0.6 : 1,
                      cursor: !trimmedReason || verdictBlocked ? 'default' : 'pointer',
                    }}
                  >
                    Submit rejection
                  </button>
                </div>
              </div>
            ) : confirmingCopy ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span
                  style={{
                    fontFamily: typo.family.sans,
                    fontSize: typo.size.body,
                    lineHeight: 1.5,
                    color: statusColors.warn,
                  }}
                >
                  The client approved this ad as it stands. Saving your changes removes their
                  approval, and the ad goes back for review.
                </span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setConfirmingCopy(false)}
                    disabled={savingCopy}
                    style={modalGhostButtonStyle(dark)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCopy}
                    disabled={savingCopy}
                    style={{
                      ...modalPrimaryButtonStyle(dark),
                      flex: '0 0 auto',
                      opacity: savingCopy ? 0.6 : 1,
                    }}
                  >
                    Save and clear the approval
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {uploading && (
                  <Note tone={dark.textMuted}>
                    <CircularProgress size={14} style={{ color: dark.accent }} />
                    Uploading…
                  </Note>
                )}
                {uploadError && <Note tone={statusColors.danger}>{uploadError}</Note>}
                {!uploading && hasStagedVideo && (
                  <Note tone={dark.textMuted}>{PROCESSING_NOTE}</Note>
                )}
                {copyDirty && hasVerdict && (
                  <Note tone={statusColors.warn}>Save your changes first, or discard them.</Note>
                )}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {hasVerdict && (
                    <>
                      <VerdictButton
                        verdict="approve"
                        undo={
                          canRemoveApproval
                            ? { label: 'Remove approval', icon: 'times' }
                            : undefined
                        }
                        wide
                        recorded={isApproved}
                        onClick={isApproved ? onRemoveApproval : handleApprove}
                        disabled={verdictBlocked || (isApproved && !canRemoveApproval)}
                        dimmed={verdictBlocked}
                      />
                      <VerdictButton
                        verdict="reject"
                        undo={{ label: 'Change reason', icon: 'pencil' }}
                        recorded={isRejected}
                        onClick={() => setRejecting(true)}
                        disabled={verdictBlocked}
                        dimmed={verdictBlocked}
                      />
                      {canClearRejection && (
                        <button
                          type="button"
                          onClick={onRemoveApproval}
                          disabled={verdictBlocked}
                          style={{
                            ...modalGhostButtonStyle(dark),
                            gap: 7,
                            flex: '0 0 auto',
                            opacity: verdictBlocked ? 0.6 : 1,
                            cursor: verdictBlocked ? 'default' : 'pointer',
                          }}
                        >
                          <i className="fa fa-fw fa-undo" aria-hidden="true" />
                          Clear rejection
                        </button>
                      )}
                    </>
                  )}
                  {/* Right of the verdicts and on their row: saving copy is a
                    different kind of act, but it cost a whole band of the dialog
                    — and a second hairline — to say so on a row of its own.

                    Both appear only against unsaved work — an upload in flight
                    counts, because Discard is how it is abandoned — so at rest
                    the bar is the verdicts and nothing else. What says the ad can
                    be edited is that the column is fields rather than text; a
                    permanently greyed Save adds no information to that. This one
                    condition also covers who never sees them at all: a client is
                    handed no `onSaveCopy` and a published ad's fields are
                    disabled, so neither can make a draft dirty. */}
                  {(copyDirty || uploading) && (
                    <>
                      {/* Deliberately live during an upload: abandoning a slow
                        one is the only thing Discard is for at that moment. A
                        save in flight is different — its failure would have
                        nowhere to surface. */}
                      <button
                        type="button"
                        onClick={discardCopy}
                        disabled={submitting || savingCopy}
                        style={{
                          marginLeft: 'auto',
                          padding: 0,
                          border: 'none',
                          background: 'transparent',
                          fontFamily: typo.family.sans,
                          fontSize: typo.size.body,
                          color: dark.textMuted,
                          cursor: submitting || savingCopy ? 'default' : 'pointer',
                          opacity: submitting || savingCopy ? 0.6 : 1,
                        }}
                      >
                        Discard
                      </button>
                      <button
                        type="button"
                        onClick={requestSaveCopy}
                        disabled={busy || cannotSave}
                        style={{
                          ...modalPrimaryButtonStyle(dark),
                          flex: '0 0 auto',
                          opacity: busy || cannotSave ? 0.6 : 1,
                          cursor: busy || cannotSave ? 'default' : 'pointer',
                        }}
                      >
                        {savingCopy ? 'Saving…' : 'Save'}
                      </button>
                    </>
                  )}
                </div>
                {saveError && <Note tone={statusColors.danger}>{saveError}</Note>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* One input for every media action in the dialog; `requestUpload` records
        which one asked. */}
      {onUploadMedia && (
        <input
          ref={fileInput}
          type="file"
          accept={REVIEW_MEDIA_ACCEPT}
          hidden
          onChange={event => {
            const file = event.target.files?.[0]
            if (file) handleFile(file)
          }}
        />
      )}
    </PacketModalShell>
  )
}
